import React from "react";
import Reveal from "@/components/sections/Reveal";
import SpeakingForm from "@/components/forms/SpeakingForm";
import { useSiteImages } from "@/lib/useSiteImages";

const additionalTopics = [
  "The Fractional Executive: What It Is, What It Isn't, and Why the Model Works",
  "Building Equitable Technology: What Diversity in Infrastructure Actually Means",
  "400 Products in 20 Years: Patterns and Decisions Across the Full Stack",
  "Governance That Holds: Architecture for AI, Data, and Institutional Trust",
];

const pastStages = [
  { stage: "CES (Consumer Electronics Show)", year: "2015, 2016, 2020, 2025, 2026" },
  { stage: "Open Source in Finance Forum, New York", year: "2015, 2025" },
  { stage: "DevOpsDays New York", year: "2016, 2017" },
  { stage: "Dreamforce", year: "2013–2026" },
  { stage: "Texas Dreamin (Salesforce community)", year: "Multiple years" },
  { stage: "Equality Lounge @ CES", year: "2025" },
  { stage: "Obama White House Girls in STEM Initiative", year: "Historical" },
];

export default function Speaking() {
  const IMAGES = useSiteImages();
  return (
    <div>
      {/* HERO */}
      <section
        className="rk-section"
        style={{ paddingTop: "calc(var(--rk-section-py) + 60px)" }}
      >
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="rk-eyebrow">Speaking</div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="rk-heading-page mt-5">
                  On stages where the decisions get made.
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-8 rk-body"
                  style={{ maxWidth: 640, color: "var(--rk-ink)" }}
                >
                  Rakia Finley has spoken at CES, the Open Source in Finance
                  Forum, DevOpsDays, and executive events across the United
                  States and internationally. She brings 20+ years of direct
                  build experience to conversations about AI governance,
                  enterprise technology, and the leadership decisions that
                  shape what organizations are actually capable of. Her talks
                  are not advisory. They are practitioner accounts — drawn
                  from engagements at the White House, in federal agencies,
                  at Fortune 500 companies, and in early-stage companies
                  navigating their first major technology architecture
                  decision.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.08} y={28}>
                <figure
                  style={{
                    position: "relative",
                    aspectRatio: "4/5",
                    overflow: "hidden",
                    borderRadius: 2,
                    border: "1px solid var(--rk-border)",
                  }}
                >
                  <img
                    src={IMAGES.speakingPortrait}
                    alt="Rakia Finley speaking at an event"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      filter: "saturate(0.96) contrast(1.02)",
                    }}
                    loading="eager"
                  />
                </figure>
                <figcaption className="mt-3 rk-caption">
                  Speaking engagement — practitioner accounts, not advisory.
                </figcaption>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE TALK */}
      <section
        className="rk-section-tight"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="rk-eyebrow">Signature talk</div>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.05}>
                <h2 className="rk-heading-section" style={{ letterSpacing: "-0.015em" }}>
                  The Infrastructure Gap: Why Most AI Strategies Fail Before
                  They Launch.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p
                  className="mt-7 rk-body"
                  style={{ color: "var(--rk-ink)", maxWidth: 720 }}
                >
                  Organizations invest heavily in AI strategy. Most of those
                  strategies fail — not because the strategy was wrong, but
                  because the infrastructure was not ready to hold it. Rakia
                  draws on two decades of AI readiness work — including
                  assessments for the White House and the Department of
                  Energy — to give technical and non-technical audiences a
                  clear picture of what readiness actually requires, and what
                  it costs to skip it.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div
                    className="py-4"
                    style={{
                      borderTop: "1px solid var(--rk-copper)",
                    }}
                  >
                    <div className="rk-eyebrow">Formats</div>
                    <p className="mt-3 rk-body" style={{ color: "var(--rk-ink)" }}>
                      Keynote (45–60 min) · Executive panel · Workshop
                      (half-day)
                    </p>
                  </div>
                  <div
                    className="py-4"
                    style={{
                      borderTop: "1px solid var(--rk-copper)",
                    }}
                  >
                    <div className="rk-eyebrow">Audience</div>
                    <p className="mt-3 rk-body" style={{ color: "var(--rk-ink)" }}>
                      Enterprise executives, board members, founders,
                      government technology officers.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ADDITIONAL TOPICS */}
      <section className="rk-section">
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Additional talk topics</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Beyond the keynote.</h2>
          </Reveal>
          <div className="mt-12">
            {additionalTopics.map((t, i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div
                  className="py-8 grid grid-cols-12 gap-4 items-baseline"
                  style={{
                    borderTop: "1px solid var(--rk-border)",
                    borderBottom:
                      i === additionalTopics.length - 1
                        ? "1px solid var(--rk-border)"
                        : "none",
                  }}
                >
                  <div
                    className="col-span-2 md:col-span-1 rk-caption"
                    style={{ color: "var(--rk-copper)" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 md:col-span-11">
                    <h3
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "clamp(22px, 2.2vw, 30px)",
                        lineHeight: 1.2,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {t}
                    </h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PAST STAGES */}
      <section
        className="rk-section"
        style={{ backgroundColor: "var(--rk-surface)" }}
      >
        <div className="rk-container-wide">
          <Reveal>
            <div className="rk-eyebrow">Past stages</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="rk-heading-section mt-5">Where she has spoken.</h2>
          </Reveal>
          <div
            className="mt-10 overflow-hidden"
            style={{ border: "1px solid var(--rk-border)" }}
          >
            <table
              className="w-full"
              style={{
                borderCollapse: "collapse",
                fontFamily: "var(--font-body)",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "var(--rk-bg)" }}>
                  <th
                    className="text-left py-4 px-5 rk-eyebrow"
                    style={{ borderBottom: "1px solid var(--rk-copper)" }}
                  >
                    Stage / Event
                  </th>
                  <th
                    className="text-left py-4 px-5 rk-eyebrow"
                    style={{ borderBottom: "1px solid var(--rk-copper)" }}
                  >
                    Year / Notes
                  </th>
                </tr>
              </thead>
              <tbody>
                {pastStages.map((row, i) => (
                  <tr
                    key={row.stage}
                    style={{
                      borderTop:
                        i === 0 ? "none" : "1px solid var(--rk-border)",
                    }}
                  >
                    <td
                      className="py-5 px-5 rk-body"
                      style={{ color: "var(--rk-ink)" }}
                    >
                      {row.stage}
                    </td>
                    <td
                      className="py-5 px-5 rk-body"
                      style={{ color: "var(--rk-muted)" }}
                    >
                      {row.year}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="rk-section">
        <div className="rk-container-text">
          <Reveal>
            <p
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "clamp(26px, 2.6vw, 36px)",
                lineHeight: 1.25,
                letterSpacing: "-0.01em",
              }}
            >
              Rakia’s sessions are direct, substantive, and drawn from real
              engagements — not theory. Her audiences leave with a different
              frame for the decisions they’re making.
            </p>
          </Reveal>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        className="rk-section"
        style={{
          backgroundColor: "var(--rk-dark)",
          color: "var(--rk-cream)",
        }}
      >
        <div className="rk-container-text">
          <Reveal>
            <div className="rk-eyebrow" style={{ color: "rgba(250,247,242,0.6)" }}>
              Book Rakia
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="rk-heading-section mt-5"
              style={{ color: "var(--rk-cream)" }}
            >
              Speaking inquiry.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-6"
              style={{
                color: "rgba(250,247,242,0.8)",
                fontSize: "clamp(16px, 1.2vw, 18px)",
                lineHeight: 1.6,
                maxWidth: 560,
              }}
            >
              Share your event. Rakia reviews each request personally.
            </p>
          </Reveal>
          <div
            className="mt-12 p-6 md:p-10"
            style={{
              backgroundColor: "var(--rk-bg)",
              color: "var(--rk-ink)",
              borderRadius: 2,
              border: "1px solid var(--rk-copper)",
            }}
          >
            <SpeakingForm />
          </div>
        </div>
      </section>
    </div>
  );
}
